/* Tasks — the completion list.
   No mockup exists for this screen; the visual reference is `docs/design/visits.html` (the same
   archetype) and the normative rules are `docs/design/spec/`.

   Shape (spec/03 archetype A): page head → filter card → status segment + derived chips → table →
   sticky bulk bar. Filters apply as they change and everything narrows on the SERVER — the page
   only ever holds one page of rows, so it must never filter or sort them itself.

   Read this before changing the query: **none of these filters worked before this rewrite.**
   `TaskViewSet` carried `filter_class = TaskFilter`, the attribute name django-filter dropped in
   2.0, and picsell-api pins 2.4.0 — so `get_filterset_class` returned None, `filter_queryset` was
   a pass-through, and every param the old page sent (status, network, template, region, point,
   created_by, supervisor, the date ranges, overdue, negative answers) was silently ignored. The
   list returned the whole company scope no matter what the panel said. picsell-api gained
   `tasks/api/web/v1/filters.py` (`TaskWebFilter`) and the correct attribute in the same change,
   plus `status_counts` on the paginated response for the segment.

   `status` is a queryset annotation now (`annotate_task_status`), not the old per-object English
   label — which is what lets it be filtered, sorted and counted, and lets the four state words
   live here in four locales. It has four states where the old one had three: "nobody answered"
   and "answered and rejected" were both `Not Completed`, and they are different jobs. */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, Checkbox, Col, Dropdown, Empty, Flex, Form, Input, Modal, Row, Segmented, Space, Table, Tag, Typography } from "antd";
import {
  ApartmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ClusterOutlined,
  CompassOutlined,
  DownOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  IdcardOutlined,
  OrderedListOutlined,
  PictureOutlined,
  PlusOutlined,
  PushpinOutlined,
  SearchOutlined,
  StopOutlined,
  TableOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import { PersonCell, RowActions, StatusIcon, TableFoot, actionsColumn, statusColumn, useTableScrollY } from "../../component/table-cells";
import PeriodPicker, { clampRange, defaultRange } from "../../component/PeriodPicker";
import { ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, SIDE_NAV, TASK_LIST } from "../../config/lang";
import { call, getAbortable } from "../../util/action";
import dayjs from "../../util/dayjs";
import { fill, regionId, translate } from "../../util/util";
import { localeFor } from "../merchandiser/route/dateUtils";

import "./tasks.scss";

const PAGE_SIZES = [10, 20, 50, 100];

/**
 * Widest period the list will accept, in calendar months — the same cap `/photo-review` enforces,
 * and the default window is the shared `defaultRange()` (two weeks, today inclusive) so the two
 * pages cannot drift apart.
 *
 * Enforced in the same THREE places, for the same reason: the calendar greys out days beyond it,
 * `searchToState` clamps a pasted URL, and `buildQuery` clamps whatever is in state at request
 * time. The last one is the backstop that makes the other two convenience rather than the rule.
 */
const MAX_RANGE_MONTHS = 3;

/* The working-set split. Values are the server's `status` enum verbatim (`TaskWebStatus`). */
const STATUS_SEGMENTS = [
  ["all", "seg_all"],
  ["awaiting", "st_awaiting"],
  ["on_review", "st_on_review"],
  ["done", "st_done"],
  ["rejected", "st_rejected"],
];

const STATUS_ICON = {
  awaiting: { Icon: ClockCircleOutlined, tone: "wait", key: "st_awaiting" },
  on_review: { Icon: ExclamationCircleOutlined, tone: "wait", key: "st_on_review" },
  done: { Icon: CheckCircleOutlined, tone: "ok", key: "st_done" },
  rejected: { Icon: CloseCircleOutlined, tone: "no", key: "st_rejected" },
};

/* A deactivated task is a resting state, not a fault, so it takes the neutral tone and outranks
   the completion status in the glyph — the row is closed either way. */
const INACTIVE_ICON = { Icon: StopOutlined, tone: "off", key: "st_inactive" };

/* The derived, counted first-class filters (spec/03: "a filter without a count makes the user
   click to discover emptiness"). `param` is what the server calls it. */
const CHIPS = [
  { key: "overdue", param: "overdue", count: "overdue", label: "chip_overdue", hint: "chip_overdue_hint" },
  { key: "negative", param: "with_negative_answers", count: "negative", label: "chip_negative", hint: "chip_negative_hint" },
  { key: "inactive", param: "is_active", count: "inactive", label: "chip_inactive", hint: "chip_inactive_hint", invert: true },
];

/* Column key → the field the API orders on. A key missing here is NOT sortable: `priority` and
   `components` come from the template, and anything computed per object could only reorder the
   page already loaded, which reads as a sort and is not one. */
const ORDERING = {
  task: "title",
  type: "template__name",
  point: "point__name",
  deadline: "end",
  answers: "answers_count",
  photos: "photos_count",
  status: "status",
};

/* Seven tracks with the period spanning two — nine units, and 24 does not divide by nine. A
   `<Col>`'s responsive value may be an object rather than a span, and that object takes `flex`.
   `0 0` rather than `1 1`: a short last row leaves empty tracks instead of stretching. */
const FIELD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 50%" }, md: { flex: "0 0 33.3333%" }, xl: { flex: "0 0 12.5%" } };
const PERIOD_COL = { xs: { flex: "0 0 100%" }, sm: { flex: "0 0 100%" }, md: { flex: "0 0 66.6667%" }, xl: { flex: "0 0 25%" } };

const blankFilter = () => ({ county: "", network: "", template: "", merchandiser: "", supervisor: [], points: [], search: "" });

const SINGLE_KEYS = ["county", "network", "template", "merchandiser"];
const ARRAY_KEYS = ["supervisor", "points"];

/** Which ENTITY_DROPDOWN_SETTINGS key names each filter's chip. */
const CHIP_LABEL = {
  county: "region",
  network: "network",
  template: "template",
  merchandiser: "merchandisers",
  supervisor: "supervisor",
  points: "points",
};

/** Which TASK_LIST key names each toggleable column in the Columns panel. */
const COLUMN_LABEL = {
  type: "col_type",
  point: "col_point",
  merchandiser: "k_merchandiser",
  answers: "col_answers",
  photos: "col_photos",
  author: "col_author",
};
const TOGGLEABLE = Object.keys(COLUMN_LABEL);

/* One request per record — there is no bulk endpoint — in batches, so a 100-row selection does not
   open 100 sockets at once. */
const BULK_CHUNK = 6;

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const joinIds = list =>
  (list || [])
    .map(idOf)
    .filter(v => v !== undefined && v !== null && v !== "")
    .join(",");

const isoDay = d => dayjs(d).format("YYYY-MM-DD");

/**
 * The request the table runs on.
 *
 * Built here rather than through `filterUrl()` so the per-field shape the endpoint expects is
 * visible in one place — and because `filterUrl` interpolates its search term unencoded.
 *
 * The period matches on `end`, the DEADLINE: this page answers "what is due and what is late", so
 * the deadline is the date a supervisor means. (`start` would hide a task created last month and
 * due tomorrow — exactly the row they are looking for.)
 */
export function buildQuery({ filter, range, status, chips, page, size, sort }) {
  const p = new URLSearchParams();
  /* Clamped here as well as in the picker and in `searchToState`: this is the one place every
     request goes through, so it is what actually holds the cap. */
  const period = clampRange(range, MAX_RANGE_MONTHS);
  p.set("page", String(page));
  p.set("page_size", String(size));

  if (filter.county) p.set("county", String(idOf(filter.county)));
  if (filter.network) p.set("networks", String(idOf(filter.network)));
  if (filter.template) p.set("templates", String(idOf(filter.template)));
  if (filter.merchandiser) p.set("merchandiser", String(idOf(filter.merchandiser)));
  if (filter.supervisor?.length) p.set("supervisor", joinIds(filter.supervisor));
  if (filter.points?.length) p.set("points", joinIds(filter.points));
  if (filter.search?.trim()) p.set("search", filter.search.trim());
  if (status !== "all") p.set("status", status);

  CHIPS.forEach(chip => {
    if (!chips.includes(chip.key)) return;
    /* `invert` is the "deactivated" chip: the flag it reads is `is_active`, so asking for the
       deactivated ones means sending false. Unchecked sends NOTHING — the server treats a `false`
       on the other flags as "don't care" now, but an absent param is unambiguous either way. */
    p.set(chip.param, chip.invert ? "false" : "true");
  });

  if (period?.[0]) p.set("end__gte", isoDay(period[0]));
  /* Inclusive end: `end__lte` against a bare date is midnight, so the picked day itself would drop
     every task not due at exactly 00:00. */
  if (period?.[1]) p.set("end__lte", isoDay(dayjs(period[1]).add(1, "day")));
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);

  return p.toString();
}

/** URL ↔ state, so a filtered view is shareable and survives Back from a task's detail page. */
function stateToSearch({ filter, range, status, chips, page, size, sort }) {
  const p = new URLSearchParams();
  if (range?.[0]) p.set("from", isoDay(range[0]));
  if (range?.[1]) p.set("to", isoDay(range[1]));
  SINGLE_KEYS.forEach(key => {
    if (filter[key]) p.set(key, String(idOf(filter[key])));
  });
  ARRAY_KEYS.forEach(key => (filter[key] || []).forEach(v => p.append(key, String(idOf(v)))));
  if (filter.search?.trim()) p.set("q", filter.search.trim());
  if (status !== "all") p.set("status", status);
  chips.forEach(key => p.append("chip", key));
  if (page > 1) p.set("page", String(page));
  if (size !== PAGE_SIZES[0]) p.set("size", String(size));
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString();
}

export function searchToState(search, me) {
  const p = new URLSearchParams(search);
  const filter = blankFilter();
  SINGLE_KEYS.forEach(key => {
    if (p.get(key)) filter[key] = p.get(key);
  });
  /* Ids only — `EntitySelect` names them once it has fetched the option they belong to. */
  ARRAY_KEYS.forEach(key => {
    filter[key] = p.getAll(key).map(v => ({ id: Number(v) }));
  });
  filter.search = p.get("q") || "";

  const hasFilters = Boolean(search && search.replace(/^\?/, ""));
  /* Without a URL to restore, the supervisor's own region is the scope they are responsible for —
     the same default the old page seeded. */
  if (!hasFilters) filter.county = regionId(me) || "";

  const from = p.get("from");
  const to = p.get("to");
  const sort = p.get("sort");
  return {
    filter,
    range: from && to ? clampRange([dayjs(from), dayjs(to)], MAX_RANGE_MONTHS) : defaultRange(),
    status: STATUS_SEGMENTS.some(([value]) => value === p.get("status")) ? p.get("status") : "all",
    chips: p.getAll("chip").filter(key => CHIPS.some(chip => chip.key === key)),
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(Number(p.get("size"))) ? Number(p.get("size")) : PAGE_SIZES[0],
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

/* The deadline cell's own reading: overdue and not done is the thing this page exists to surface,
   so it is said in the row rather than left to the reader to compute against today. */
const isOverdue = row => Boolean(row.end) && row.is_active !== false && row.status !== "done" && dayjs(row.end).isBefore(dayjs());

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(TASK_LIST, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);

  /* Read once. After mount this component owns the state and writes the URL, not the reverse —
     reading it back on every change would fight the user's own typing. */
  const initial = useRef(searchToState(location.search, me)).current;

  const [filter, setFilter] = useState(initial.filter);
  const [range, setRangeState] = useState(initial.range);
  /* A cleared period returns to the default window instead of "all time": the cap exists so this
     list never asks for a year of tasks, and clearing to nothing would walk straight past it. */
  const setRange = next => setRangeState(clampRange(next, MAX_RANGE_MONTHS) || defaultRange());
  const [status, setStatus] = useState(initial.status);
  const [chips, setChips] = useState(initial.chips);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  const [sort, setSort] = useState(initial.sort);

  /* What the user is typing, kept apart from `filter.search` so the request is debounced without
     the input lagging a keystroke behind the caret. */
  const [term, setTerm] = useState(initial.filter.search);

  /* The option objects the selects handed back, so a chosen value labels itself. */
  const [chosen, setChosen] = useState({ county: null, network: null, template: null, merchandiser: null });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState([]);

  /* --------------------------------------------------------------- fetch */

  const query = useMemo(() => buildQuery({ filter, range, status, chips, page, size, sort }), [filter, range, status, chips, page, size, sort]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getAbortable(`tasks/?${query}`, controller.signal)
      .then(response => {
        if (!response) return;
        setRows(response.results || []);
        setTotal(response.count || 0);
        if (response.status_counts) setCounts(response.status_counts);
        setFailed(false);
        setLoading(false);
      })
      .catch(error => {
        if (error.name === "AbortError") return;
        setRows([]);
        setTotal(0);
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query]);

  /* The URL trails the state; `replace` so filtering does not fill the history stack and Back
     still leaves the page rather than undoing one filter at a time. */
  useEffect(() => {
    const search = stateToSearch({ filter, range, status, chips, page, size, sort });
    if (search !== location.search.replace(/^\?/, "")) history.replace({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, range, status, chips, page, size, sort]);

  /* Only the free-text field is debounced; every other control applies on change. */
  useEffect(() => {
    if (term === filter.search) return;
    const timer = setTimeout(() => {
      setPage(1);
      setSelected([]);
      setFilter(prev => ({ ...prev, search: term }));
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  /* Any narrowing invalidates the page number: page 7 of the old result set is usually past the
     end of the new one, which would render an empty table over a non-empty result. */
  const narrow = fn => (...args) => {
    setPage(1);
    setSelected([]);
    fn(...args);
  };

  const setSingle = narrow((key, option) => {
    setChosen(prev => ({ ...prev, [key]: option }));
    setFilter(prev => ({ ...prev, [key]: option ? idOf(option) : "" }));
  });
  const setArray = narrow((key, list) => setFilter(prev => ({ ...prev, [key]: list })));
  const toggleChip = narrow(key => setChips(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])));

  const resetFilters = narrow(() => {
    setFilter(blankFilter());
    setChosen({ county: null, network: null, template: null, merchandiser: null });
    setRange(defaultRange());
    setStatus("all");
    setChips([]);
    setTerm("");
    setSort(null);
  });

  /* ---------------------------------------------------------------- chips */

  const { labels, remember, nameFor } = useLabelCache();

  const clearChip = narrow((key, id) => {
    if (key === "period") {
      setRange(defaultRange());
      return;
    }
    if (key === "search") {
      setTerm("");
      setFilter(prev => ({ ...prev, search: "" }));
      return;
    }
    if (SINGLE_KEYS.includes(key)) {
      setChosen(prev => ({ ...prev, [key]: null }));
      setFilter(prev => ({ ...prev, [key]: "" }));
      return;
    }
    setFilter(prev => ({ ...prev, [key]: (prev[key] || []).filter(v => String(idOf(v)) !== String(id)) }));
  });

  const activeChips = useMemo(() => {
    const out = [];
    if (range?.[0] && range?.[1]) {
      /* Short form, per spec/04 — `28 Jul → 11 Aug`. The year shows only when the range leaves the
         current one, which is also what keeps the chip on one line on a phone. */
      const thisYear = new Date().getFullYear();
      const sameYear = range[0].year() === thisYear && range[1].year() === thisYear;
      const fmt = new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
      out.push({ key: "period", label: t("col_deadline"), value: `${fmt.format(range[0].toDate())} → ${fmt.format(range[1].toDate())}` });
    }
    if (filter.search) out.push({ key: "search", label: tp("search"), value: filter.search });
    SINGLE_KEYS.forEach(key => {
      if (!filter[key]) return;
      /* `chosen[key]` is the option object the select handed back, so it names itself; falling back
         to the bare id sends `nameFor` to the resolution cache. */
      out.push({ key, label: td(CHIP_LABEL[key]), value: nameFor(key, chosen[key] ?? filter[key]) });
    });
    ARRAY_KEYS.forEach(key => {
      (filter[key] || []).forEach(v => out.push({ key, id: idOf(v), label: td(CHIP_LABEL[key]), value: nameFor(key, v) }));
    });
    return out;
    /* `labels` is a dependency: a chip that arrived as an id has to re-render once its name lands. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, chosen, labels, range, lang]);

  /* --------------------------------------------------------- deactivate */

  /* PATCH, never DELETE. `Task.delete()` sets `is_active = False` when the task has answers but
     **hard-deletes when it has none** — one control doing two irreversibly different things
     depending on state the user cannot see. PATCH means one verb with one meaning. */
  const deactivate = task =>
    new Promise(resolve => {
      call({ method: "PATCH", url: `tasks/${task.id}/`, post: { is_active: false } }, response => resolve(Boolean(response)));
    });

  /* Rows are patched in place rather than refetched, so the table does not jump under the cursor.
     A row that no longer matches the active filters stays until the next fetch: its glyph has
     already changed, so it is showing the result of the action rather than a stale state. */
  const applyLocally = ids => {
    setRows(prev => prev.map(row => (ids.includes(row.id) ? { ...row, is_active: false } : row)));
    setCounts(prev => ({ ...prev, inactive: (prev.inactive || 0) + ids.length }));
  };

  const runDeactivate = async targets => {
    if (!targets.length) return;
    setBusy(true);
    const done = [];
    for (let i = 0; i < targets.length; i += BULK_CHUNK) {
      const batch = targets.slice(i, i + BULK_CHUNK);
      const results = await Promise.all(batch.map(deactivate));
      batch.forEach((task, index) => results[index] && done.push(task.id));
    }
    setBusy(false);
    if (done.length) {
      applyLocally(done);
      Alert.success(fill(t("deactivated_msg"), { n: done.length }), { position: "top-right" });
    }
    if (done.length < targets.length) {
      Alert.warning(fill(t("change_failed_msg"), { n: targets.length - done.length }), { position: "top-right" });
    }
    setSelected([]);
  };

  /* Confirm states WHAT and HOW MANY, and the button carries the verb, never "OK". `Modal.confirm`
     is a static call: it renders outside the tree and does not inherit AppShell's
     `ConfigProvider locale`, so both button labels are passed explicitly. */
  const confirmDeactivate = targets => {
    if (!targets.length) return;
    Modal.confirm({
      title: fill(t("confirm_deactivate"), { n: targets.length }),
      content: t("confirm_deactivate_desc"),
      okText: t("deactivate"),
      okButtonProps: { danger: true },
      cancelText: t("cancel_btn"),
      onOk: () => runDeactivate(targets),
    });
  };

  /* Walk into the record carrying this row's position in the FILTERED list and the filters
     themselves, so the detail page can come back to exactly this view. */
  const openTask = task => {
    const search = stateToSearch({ filter, range, status, chips, page, size, sort });
    history.push(`/task/${task.id}${search ? `?${search}` : ""}`);
  };

  /* ------------------------------------------------------------- columns */

  const dateFmt = useMemo(() => new Intl.DateTimeFormat(localeFor(lang), { day: "2-digit", month: "short", year: "numeric" }), [lang]);

  const columns = useMemo(() => {
    const all = [
      statusColumn((value, row) => {
        const { Icon, tone, key } = row.is_active === false ? INACTIVE_ICON : STATUS_ICON[row.status] || STATUS_ICON.awaiting;
        return <StatusIcon Icon={Icon} tone={tone} label={t(key)} />;
      }),
      {
        key: "task",
        title: t("col_task"),
        width: 280,
        fixed: "left",
        ellipsis: true,
        sorter: true,
        render: (value, row) => (
          <Flex vertical className="lp-main">
            <Typography.Text strong ellipsis={{ tooltip: row.title }}>
              {row.title}
            </Typography.Text>
            <Typography.Text type="secondary" className="lp-main__sub" ellipsis={{ tooltip: row.description }}>
              {row.description || "—"}
            </Typography.Text>
          </Flex>
        ),
      },
      {
        key: "type",
        title: t("col_type"),
        width: 160,
        ellipsis: true,
        sorter: true,
        render: (value, row) => row.template?.name || <span className="lp-dash">—</span>,
      },
      {
        key: "point",
        title: t("col_point"),
        width: 240,
        ellipsis: true,
        sorter: true,
        render: (value, row) => {
          if (!row.point) return <span className="lp-dash">—</span>;
          const meta = [row.point.locality?.name, row.point.network?.name].filter(Boolean).join(" · ");
          return (
            <Flex vertical className="lp-main">
              <Typography.Text ellipsis={{ tooltip: row.point.name }}>{row.point.name}</Typography.Text>
              <Typography.Text type="secondary" className="lp-main__sub" ellipsis={{ tooltip: meta }}>
                {meta || "—"}
              </Typography.Text>
            </Flex>
          );
        },
      },
      {
        key: "merchandiser",
        title: t("k_merchandiser"),
        width: 190,
        ellipsis: true,
        /* Derived from the point — a task carries no merchandiser of its own, which is also why a
           point with nobody responsible makes the task nobody's. */
        render: (value, row) => <PersonCell user={row.point?.responsible_merchandiser} strong={false} />,
      },
      {
        key: "deadline",
        title: t("col_deadline"),
        width: 150,
        sorter: true,
        render: (value, row) => {
          if (!row.end) return <span className="lp-dash">—</span>;
          const late = isOverdue(row);
          return (
            <Flex vertical className="lp-main">
              <Typography.Text className={late ? "tk-late" : undefined}>{dateFmt.format(new Date(row.end))}</Typography.Text>
              {late ? (
                <Typography.Text type="secondary" className="lp-main__sub tk-late">
                  {t("chip_overdue")}
                </Typography.Text>
              ) : null}
            </Flex>
          );
        },
      },
      {
        key: "answers",
        title: t("col_answers"),
        width: 110,
        align: "right",
        sorter: true,
        render: (value, row) => {
          const n = row.answers_count || 0;
          const negative = row.negative_answers_count || 0;
          if (!n) return <span className="lp-dash">—</span>;
          return (
            <Space size={4}>
              <span className="lp-num">{n}</span>
              {/* The count and the noun stay apart — never `{n} negative answers` (spec/04). */}
              {negative ? (
                <Tag className="tk-tag--no" title={t("chip_negative_hint")}>
                  {negative}
                </Tag>
              ) : null}
            </Space>
          );
        },
      },
      {
        key: "photos",
        title: t("col_photos"),
        width: 100,
        align: "right",
        sorter: true,
        render: (value, row) =>
          row.photos_count ? (
            <Space size={4}>
              <PictureOutlined className="tk-cell-icon" />
              <span className="lp-num">{row.photos_count}</span>
            </Space>
          ) : (
            <span className="lp-dash">—</span>
          ),
      },
      {
        key: "author",
        title: t("col_author"),
        width: 190,
        ellipsis: true,
        render: (value, row) => <PersonCell user={row.created_by} strong={false} />,
      },
      actionsColumn((value, row) => (
        <RowActions
          label={t("row_actions")}
          items={[
            { key: "open", icon: <ExportOutlined />, label: t("open") },
            ...(row.is_active === false ? [] : [{ key: "off", icon: <StopOutlined />, label: t("deactivate"), danger: true }]),
          ]}
          onClick={key => (key === "open" ? openTask(row) : confirmDeactivate([row]))}
        />
      )),
    ];
    return all.filter(column => !hidden.includes(column.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, hidden, rows, dateFmt]);

  const scrollY = useTableScrollY();

  /* "Nothing here yet" and "nothing matches these filters" are different states needing different
     copy and different actions (spec/03). Anything narrowing means the latter. */
  const isFiltered =
    Boolean(filter.search || chips.length || status !== "all") || SINGLE_KEYS.some(key => filter[key]) || ARRAY_KEYS.some(key => (filter[key] || []).length);

  const selectable = rows.filter(row => selected.includes(row.id) && row.is_active !== false);

  return (
    <Flex vertical gap="middle" className="shell-content lp tk">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {translate(SIDE_NAV, "tasks", lang)}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
        {/* Three targets because the API takes three; a network or a group fans out server-side
            into one task per point, which is why they are one menu and not three pages. */}
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "point", icon: <PushpinOutlined />, label: t("new_for_point") },
              { key: "network", icon: <ClusterOutlined />, label: t("new_for_network") },
              { key: "point_group", icon: <ApartmentOutlined />, label: t("new_for_group") },
            ],
            onClick: ({ key }) => history.push(`/task/new/${key}`),
          }}>
          <Button type="primary" icon={<PlusOutlined />}>
            <Space size={4}>
              {t("new_task")}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: there is nothing to submit — filters apply as they change, and a
            real <form> would turn Enter in the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <Row gutter={[12, 12]}>
            <Col {...PERIOD_COL}>
              <Form.Item label={t("col_deadline")}>
                {/* `allowFuture` because this filters on the DEADLINE — deadlines are future
                    dates, and PeriodPicker greys out the future by default, which would make it
                    impossible to ask "what is due next week" on the very field it filters. */}
                <PeriodPicker value={range} onChange={narrow(setRange)} maxMonths={MAX_RANGE_MONTHS} clearTo={defaultRange} allowFuture />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("region")}>
                <EntitySelect
                  entity="county"
                  value={filter.county}
                  seed={chosen.county || (me?.county && typeof me.county === "object" ? me.county : null)}
                  prefix={<CompassOutlined />}
                  onChange={option => setSingle("county", option)}
                  onResolve={remember("county")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={td("network")}>
                <EntitySelect
                  entity="network"
                  value={filter.network}
                  seed={chosen.network}
                  prefix={<ClusterOutlined />}
                  onChange={option => setSingle("network", option)}
                  onResolve={remember("network")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              {/* The template IS the task's type — what the merchandiser will be asked for — so it
                  is the most operationally meaningful filter on this row. */}
              <Form.Item label={t("col_type")}>
                <EntitySelect
                  entity="template"
                  value={filter.template}
                  seed={chosen.template}
                  prefix={<OrderedListOutlined />}
                  onChange={option => setSingle("template", option)}
                  onResolve={remember("template")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={t("k_merchandiser")}>
                <EntitySelect
                  entity="merchandisers"
                  value={filter.merchandiser}
                  seed={chosen.merchandiser}
                  prefix={<IdcardOutlined />}
                  onChange={option => setSingle("merchandiser", option)}
                  onResolve={remember("merchandiser")}
                />
              </Form.Item>
            </Col>
            {/* Only a manager sees every supervisor's tasks, so only a manager can filter by one —
                the same gate `/visit` applies. */}
            {me?.role === "supervisor_manager" ? (
              <Col {...FIELD_COL}>
                <Form.Item label={td("supervisor")}>
                  <EntitySelect
                    entity="supervisor"
                    isMulti
                    value={filter.supervisor}
                    prefix={<TeamOutlined />}
                    onChange={list => setArray("supervisor", list)}
                    onResolve={remember("supervisor")}
                  />
                </Form.Item>
              </Col>
            ) : null}
            <Col {...FIELD_COL}>
              {/* Scoped by the two filters beside it — `EntitySelect` sends `locality__county` and
                  `network`, so the list offers the points the rest of the row implies. */}
              <Form.Item label={td("points")}>
                <EntitySelect
                  entity="points"
                  isMulti
                  value={filter.points}
                  county={filter.county}
                  moduleFilter={filter.network}
                  prefix={<PushpinOutlined />}
                  onChange={list => setArray("points", list)}
                  onResolve={remember("points")}
                />
              </Form.Item>
            </Col>
            <Col {...FIELD_COL}>
              <Form.Item label={tp("search")}>
                <Input allowClear prefix={<SearchOutlined />} placeholder={t("search_ph")} value={term} onChange={event => setTerm(event.target.value)} />
              </Form.Item>
            </Col>
          </Row>

          {/* What is actually applied, in words — the selects collapse to `+N` once a filter holds
              more than one value, so without this row the only way to read the current query is to
              open each dropdown. */}
          <FilterChips className="lp-chips" items={activeChips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          {/* The derived filters, each with its count. They are `Tag.CheckableTag`s rather than a
              second Segmented because they combine: overdue AND negative is a real question. */}
          <Flex className="lp-quality" align="center" gap="small" wrap>
            {CHIPS.map(chip => (
              <Tag.CheckableTag key={chip.key} checked={chips.includes(chip.key)} onChange={() => toggleChip(chip.key)} title={t(chip.hint)}>
                <Space size={6}>
                  {t(chip.label)}
                  <span className="lp-num">{counts[chip.count] ?? 0}</span>
                </Space>
              </Tag.CheckableTag>
            ))}
          </Flex>

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented
              value={status}
              onChange={narrow(setStatus)}
              options={STATUS_SEGMENTS.map(([value, key]) => ({
                value,
                label: (
                  <Space size={6}>
                    {t(key)}
                    <Typography.Text type="secondary" className="lp-seg__n">
                      {counts[value] ?? 0}
                    </Typography.Text>
                  </Space>
                ),
              }))}
            />
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {td("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {translate(SIDE_NAV, "tasks", lang)}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {tp("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
          <span className="lp-spacer" />
          <Dropdown
            trigger={["click"]}
            /* A plain checkbox list, not a `menu` — a menu item swallows the checkbox's own click
               and closes the panel after every single toggle. */
            popupRender={() => (
              <Card className="tk-colcfg" variant="outlined" size="small">
                <Checkbox.Group
                  value={TOGGLEABLE.filter(key => !hidden.includes(key))}
                  onChange={visible => setHidden(TOGGLEABLE.filter(key => !visible.includes(key)))}>
                  <Flex vertical gap={4}>
                    {TOGGLEABLE.map(key => (
                      <Checkbox key={key} value={key}>
                        {t(COLUMN_LABEL[key])}
                      </Checkbox>
                    ))}
                  </Flex>
                </Checkbox.Group>
              </Card>
            )}>
            <Button size="small" icon={<TableOutlined />}>
              {t("columns")}
            </Button>
          </Dropdown>
        </Flex>

        <Table
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1520, y: scrollY }}
          rowClassName={row => {
            const classes = ["lp-row"];
            if (selected.includes(row.id)) classes.push("lp-row--on");
            /* An already-deactivated row recedes rather than disappearing — it is still a record. */
            if (row.is_active === false) classes.push("lp-row--off");
            return classes.join(" ");
          }}
          rowSelection={{
            fixed: true,
            selectedRowKeys: selected,
            onChange: keys => setSelected(keys),
            /* A checkbox here means exactly one thing: this row can be deactivated. */
            getCheckboxProps: row => ({ disabled: row.is_active === false }),
          }}
          onRow={row => ({
            onClick: event => {
              /* The selection cell and the row menu own their clicks; everything else walks into
                 the task. */
              if (event.target.closest(".ant-table-selection-column, .ant-dropdown, .ant-btn")) return;
              openTask(row);
            },
          })}
          /* Paging is the footer below, so the only thing left for the table's own onChange is the
             sorter — with `pagination={false}` its first argument carries nothing. */
          onChange={(pagination, filters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical gap={8} align="center">
                    <Typography.Text strong>{t(failed ? "empty_title" : isFiltered ? "empty_title" : "empty_none_title")}</Typography.Text>
                    <Typography.Text type="secondary">{t(isFiltered ? "empty_desc" : "empty_none_desc")}</Typography.Text>
                    {isFiltered ? (
                      <Button size="small" onClick={resetFilters}>
                        {td("filter_reset")}
                      </Button>
                    ) : (
                      <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => history.push("/task/new/point")}>
                        {t("new_task")}
                      </Button>
                    )}
                  </Flex>
                }
              />
            ),
          }}
        />

        <TableFoot
          id="tk"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={next => {
            setPage(next);
            setSelected([]);
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            setSelected([]);
          }}
        />
      </Card>

      {selected.length > 0 && (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(tp("w_selected"), { n: selected.length })}</Typography.Text>
          {/* "Select all" is the LOADED page, so the bar says so rather than implying the filter. */}
          <Typography.Text className="lp-bulkbar__hint">{t("select_page_hint")}</Typography.Text>
          <Space className="lp-bulkbar__acts" size={8}>
            <Button size="small" onClick={() => setSelected([])}>
              {tp("clear_selection")}
            </Button>
            <Button size="small" danger type="primary" icon={<StopOutlined />} loading={busy} onClick={() => confirmDeactivate(selectable)}>
              {t("deactivate")}
            </Button>
          </Space>
        </Flex>
      )}
    </Flex>
  );
};

export default List;
/* Named export kept: `router.js` imports `{ TaskList }`. */
export { List as TaskList };
